import { Component, inject, Inject, OnInit } from '@angular/core';

import { InfoboxService } from '@/common/infobox/infobox.service';
import { ReservationService } from '@/reservation/reservation.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PatchUserDataFullPayload, Slot, UserData, UserDataFullPayload } from '@/reservation/reservation.model';
import { TypeValueFactoryService } from '@/common/type-value-factory.service';
import { LoaderService } from '@/common/loader/loader.service';
import { ReservationSummaryApiService } from '../../../../services/reservation-summary-api.service';
import { DateService } from '@/common/date.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SlotKind } from '@/common/enums/SlotKind';
import { createQueryParams } from '@/_utils/createQueryParams';
import { DuplicateUserDataDialogComponent } from '@/reservation/reservation-summary/reservation-summary-owner/reservation-summary-owner-dialog/duplicate-user-data-dialog/duplicate-user-data-dialog.component';
import { catchError, of, finalize, switchMap, merge, tap, Observable } from 'rxjs';
import { DatePipe } from '@angular/common';
import { USER_ALREADY_EXISTS_ERROR } from '@/reservation/reservation-summary/reservation-summary-owner/reservation-summary-owner-dialog/reservation-summary-owner-dialog.component';

@Component({
	selector: 'app-reservation-accomodation-offer-participant-dialog',
	templateUrl: './reservation-accomodation-offer-participant-dialog.component.html',
	styleUrls: ['./reservation-accomodation-offer-participant-dialog.component.scss'],
})
export class ReservationAccomodationOfferParticipantDialogComponent implements OnInit {
	constructor(
		@Inject(MAT_DIALOG_DATA) public data: Slot | null,
		private readonly infobox: InfoboxService,
		private readonly _service: ReservationService,
		private readonly apiService: ReservationSummaryApiService,
		private readonly loader: LoaderService,
		private readonly dialogRef: MatDialogRef<ReservationAccomodationOfferParticipantDialogComponent>,
		private readonly dateService: DateService,
	) {}

	participantForm = new FormGroup({
		sex: new FormControl('M'),
		firstName: new FormControl('', Validators.required),
		lastName: new FormControl('', Validators.required),
		birth: new FormControl('', Validators.required),
		phone: new FormControl('', [Validators.pattern(/^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$/)]),
		regularCustomer: new FormControl(false),
		isChild: new FormControl(false),
	});

	private readonly dialog = inject(MatDialog);
	private readonly datePipe = inject(DatePipe);

	ngOnInit(): void {
		if (!this.data.userData) {
			this.data.userData = TypeValueFactoryService.createUserDataObject();
			this.data.userData.userContacts.push(TypeValueFactoryService.createUserContactObject());
		}

		this.participantForm.patchValue({
			sex: this.data.userData.sex,
			firstName: this.data.userData.firstName,
			lastName: this.data.userData.lastName,
			birth: this.data.userData.birth,
			phone: this.data.userData.userContacts[0].phone,
			regularCustomer: this.data.userData.regularCustomer,
			isChild: this.data.kind === SlotKind.CHILD,
		});
	}

	submit(): void {
		this.participantForm.markAllAsTouched();

		if (this.participantForm.invalid) {
			return this.infobox.error('Dane uczestnika zostały wypełnione nieprawidłowo');
		}

		this.loader.show('participant-change');

		const payload: UserDataFullPayload = this.createUserDataFullPayload();

		// if user data is 0 (quick reservation) then create one and attach it to the slot, if not then patch the data_full of the slot
		const request$ =
			this.data.userData.id === 0 ? this.assignNewUserDataToSlot(payload) : this.updateSlotUserData(payload);

		request$.subscribe((result) => {
			if (result) {
				this.loader.hide('participant-change');
				this.closeDialog();
				this.apiService.actions().getReservation(this._service.reservation$.getValue().id);
			}
		});
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	checkUserIsChild(date: string): void {
		const startOfTripDate = new Date(this.data.stopDeparture.timeDeparture);
		const maxAgeOfChild = new Date(
			startOfTripDate.getFullYear() - 12,
			startOfTripDate.getMonth(),
			startOfTripDate.getDate(),
		);
		const userDate = new Date(date);

		this.participantForm.get('isChild').patchValue(maxAgeOfChild < userDate);
	}

	private openDuplicateUserDataDialog(): void {
		this.loader.show('data-full');

		const dataFilters = this.buildDataFullFilters();

		const queryParams = createQueryParams(dataFilters);

		this.apiService
			.getDataFull(queryParams)
			.pipe(finalize(() => this.loader.hide('data-full')))
			.subscribe((users) =>
				this.dialog.open(DuplicateUserDataDialogComponent, {
					width: '65%',
					data: { ownerUsers: users, slotId: this.data?.id },
				}),
			);
	}

	private buildDataFullFilters(): Record<string, string> {
		return {
			birth: this.getEncodedBirth(),
			email: this.data.userData.userContacts[0].email ?? '',
			full_name: this.getEncodedFullName(),
			phone: encodeURIComponent((this.getControlValue('phone') as string) ?? ''),
		};
	}

	private getEncodedBirth(): string {
		const birth = this.participantForm.get('birth')?.value ?? '';

		return encodeURIComponent(this.datePipe.transform(birth, 'yyyy-MM-dd')) ?? '';
	}

	private getEncodedFullName(): string {
		const firstName = this.participantForm.get('firstName')?.value ?? '';
		const lastName = this.participantForm.get('lastName')?.value ?? '';
		const fullName = `${firstName} ${lastName}`.trim();
		return encodeURIComponent(fullName);
	}

	private updateSlotUserData(payload: UserDataFullPayload): Observable<string | void | UserData> {
		const patchPayload: PatchUserDataFullPayload = {
			...payload,
			id: this.data.userData.id,
		};

		return merge(
			this.apiService.patchUserDataFull(patchPayload),
			this.apiService.patchOfferSlot(this.data.id, {
				kind: this.getControlValue('isChild') ? SlotKind.CHILD : SlotKind.ADULT,
			}),
		).pipe(
			catchError((error) => {
				if (JSON.stringify(error.error) === USER_ALREADY_EXISTS_ERROR) {
					this.openDuplicateUserDataDialog();
				}
				return of(null);
			}),
			tap((result) => {
				if (result) {
					this.infobox.success('Zmieniono dane uczestnika');
				}
			}),
		);
	}

	private assignNewUserDataToSlot(payload: UserDataFullPayload): Observable<string | void> {
		return this.apiService.createUserDataFull(payload).pipe(
			switchMap((userData) => {
				if (!userData) return of(null);

				const kind = this.getControlValue('isChild') ? SlotKind.CHILD : SlotKind.ADULT;
				return this.apiService.patchOfferSlot(this.data.id, {
					kind,
					userData: userData.id,
				});
			}),
			tap((result) => {
				if (result) {
					this.infobox.success('Pomyślnie zaktualizowana dane uczestnika!');
				}
			}),
		);
	}

	private getControlValue(controlName: string): string | boolean {
		return this.participantForm.get(controlName).value;
	}

	private createUserDataFullPayload(): UserDataFullPayload {
		return {
			firstName: this.getControlValue('firstName') as string,
			lastName: this.getControlValue('lastName') as string,
			regularCustomer: this.getControlValue('regularCustomer') as boolean,
			sex: this.getControlValue('sex') as string,
			idNumber: this.data.userData.idNumber ?? '',
			passportNumber: this.data.userData.passportNumber ?? '',
			birth: this.dateService.apiToDateTime(this.getControlValue('birth') as string).date,
			userContacts: [
				{
					phone: this.getControlValue('phone') as string,
					email: this.data.userData.userContacts[0].email,
					destiny: this.data.userData.userContacts[0].destiny.id,
				},
			],
			userAddresses: [],
			userConsent: [],
		};
	}
}
